/* =============================================================================
 * delivery-clock.js -- THE ONLY PLACE THE SAME-DAY DECISION IS MADE.
 *
 * Replaces FIVE independent computations of the same thing (snippets/
 * same-day-order.liquid, sections/header.liquid, sections/product.liquid,
 * sections/cart.liquid, assets/base.js). See docs/AUDIT-countdown-consolidation.md
 * for the divergence table that made this necessary and
 * docs/BRIEF-clock-refactor.md for why it matters commercially.
 *
 * -----------------------------------------------------------------------------
 * THE STATED INVARIANT -- read this before changing any branch below.
 *
 *   THE GATE FAILS OPEN.  THE DISPLAY FAILS SILENT.
 *
 * Jason's ruling, 2026-07-20, recorded so the next reader inherits the decision
 * instead of re-deriving it from branch order:
 *
 *   - If this module cannot resolve -- metaobject missing, clock unavailable,
 *     malformed data, an exception -- the ORDER IS ALLOWED THROUGH. A wrongly
 *     accepted order is recoverable with a phone call. A wrongly REFUSED order is
 *     gone silently and you never learn it happened. Same-day is the +40%-AOV
 *     path; every refusal is an expensive one.
 *   - But the URGENCY DISPLAY renders NOTHING rather than guessing. A fabricated
 *     "2 hours 14 minutes left" is not a captured sale, it is a false promise in
 *     writing -- and it destroys the recoverability that makes fail-open sound.
 *     Silence costs one urgency impression. Take that trade every time.
 *
 * Concretely: decide().ok === false  =>  callers MUST allow the action and MUST
 * NOT render a countdown. Never invert this to be "safe". Fail-closed here is the
 * expensive direction.
 * -----------------------------------------------------------------------------
 *
 * DESIGN NOTES
 *
 * - PURE CORE. decide() is a pure function of its inputs -- no Date.now(), no
 *   globals, no DOM, no network. That is what makes the test table in
 *   scripts/delivery-clock.test.js possible: every edge case runs in a second with
 *   no browser. The BRIEF is explicit that durability here cannot come from being
 *   careful, because careful is what got skipped on busy Saturdays. It has to come
 *   from correctness being cheap to verify.
 *
 * - NO DAYJS. Timezone resolution is native Intl.DateTimeFormat, which is
 *   DST-correct and costs 0 KB. Date arithmetic is done on 'YYYY-MM-DD' strings at
 *   UTC noon, which cannot be dragged across a day boundary by a DST shift. This
 *   deletes the dayjs-bundle.js dependency -- also render-blocking lint item 4c in
 *   docs/LINT-QUEUE.md. The correctness fix pays for a perf win on the way past.
 *
 * - ONE AM/PM PARSER. There were four (AUDIT D8), two of which disagreed about
 *   whether they returned strings or ints. parseWallTime() below is the only one.
 * ============================================================================= */
(function (root, factory) {
  var api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;   // node: the test table
  if (root) root.DeliveryClock = api;                                       // browser: the three views
})(typeof self !== 'undefined' ? self : this, function () {
  'use strict';

  var MS_PER_DAY = 86400000;

  /* ---------------------------------------------------------------------------
   * Wall-clock primitives. No library, no DST hand-rolling.
   * ------------------------------------------------------------------------- */

  // What does the wall clock in `tz` read at this epoch instant?
  // Intl does the DST work. This is the one place a timezone is resolved.
  function wallClock(epochMs, tz) {
    var fmt = new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      hour12: false,
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit'
    });
    var p = {};
    fmt.formatToParts(new Date(epochMs)).forEach(function (part) { p[part.type] = part.value; });
    // Intl renders midnight as hour '24' in some ICU versions. Normalise it.
    var hour = parseInt(p.hour, 10); if (hour === 24) hour = 0;
    return {
      ymd: p.year + '-' + p.month + '-' + p.day,
      hour: hour,
      minute: parseInt(p.minute, 10),
      second: parseInt(p.second, 10)
    };
  }

  // Day-of-week of a 'YYYY-MM-DD' string. Deliberately computed in UTC: a date
  // string has no time, so no timezone can shift which weekday it names.
  // 0 = Sunday, 6 = Saturday.
  function weekdayOf(ymd) {
    var b = String(ymd).split('-');
    return new Date(Date.UTC(+b[0], +b[1] - 1, +b[2])).getUTCDay();
  }

  // Add whole days to a 'YYYY-MM-DD'. Anchored at UTC noon so a 23h or 25h DST day
  // can never round the result into the neighbouring date.
  function addDays(ymd, n) {
    var b = String(ymd).split('-');
    var d = new Date(Date.UTC(+b[0], +b[1] - 1, +b[2], 12, 0, 0));
    d.setUTCDate(d.getUTCDate() + n);
    return d.getUTCFullYear() + '-' +
      String(d.getUTCMonth() + 1).padStart(2, '0') + '-' +
      String(d.getUTCDate()).padStart(2, '0');
  }

  function isYmd(v) { return typeof v === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(v); }

  /* ---------------------------------------------------------------------------
   * THE one AM/PM parser (was AUDIT D8: four copies, two return types).
   * Accepts "1:00 PM", "1:00PM", "01:00 pm", "13:00". Returns {hour, minute} as
   * INTEGERS, or null when it cannot parse -- null propagates to fail-open rather
   * than silently becoming midnight, which is what three of the four copies did.
   * ------------------------------------------------------------------------- */
  function parseWallTime(s) {
    if (typeof s !== 'string') return null;
    var m = s.trim().match(/^(\d{1,2}):(\d{2})\s*([AaPp][Mm])?$/);
    if (!m) return null;
    var hour = parseInt(m[1], 10);
    var minute = parseInt(m[2], 10);
    if (minute > 59) return null;
    var mer = m[3] ? m[3].toUpperCase() : null;
    if (mer) {
      if (hour < 1 || hour > 12) return null;
      if (hour === 12) hour = 0;              // 12:xx AM -> 00:xx
      if (mer === 'PM') hour += 12;           // 12:xx PM -> 12:xx
    } else if (hour > 23) return null;        // 24h form
    return { hour: hour, minute: minute };
  }

  /* ---------------------------------------------------------------------------
   * THE DECISION. Pure. Everything above the return is arithmetic on the inputs.
   *
   * input = {
   *   nowMs, tz, cutoff, cutoffOverride,
   *   blockouts[], weekendEnables[], exceptions[], exceptionsActive
   * }
   * ------------------------------------------------------------------------- */
  function decide(input) {
    // Every failure path lands here. ok:false means: LET IT THROUGH, SHOW NOTHING.
    function unresolved(reason) {
      return {
        ok: false,
        reason: reason,
        today: null,
        sameDayOpen: null,
        msRemaining: null,
        cutoffWall: null,
        nextDeliveryDate: null,
        // Fail-open gate: with no data, nothing is refusable.
        isDeliverable: function () { return true; }
      };
    }

    try {
      var i = input || {};
      if (typeof i.nowMs !== 'number' || !isFinite(i.nowMs)) return unresolved('no-clock');
      var tz = i.tz || 'America/Los_Angeles';

      var now;
      try { now = wallClock(i.nowMs, tz); }
      catch (e) { return unresolved('tz-unresolvable'); }
      if (!isYmd(now.ymd)) return unresolved('tz-unresolvable');

      var clean = function (a) { return (Array.isArray(a) ? a : []).filter(isYmd); };
      var blockouts = clean(i.blockouts);
      var weekendEnables = clean(i.weekendEnables);
      var exceptions = clean(i.exceptions);

      // The vday exception list is an UNBLOCK list. When a qualifying item is in the
      // cart it (a) leaves the blockout set and (b) joins the enabled-weekend set.
      // AUDIT D2: header.liquid folded it INTO the blockouts, so two files unblocked
      // and one re-blocked. It is excluded from `blockouts` upstream by
      // snippets/delivery-window.liquid; this is the second half of the same rule.
      if (i.exceptionsActive && exceptions.length) {
        blockouts = blockouts.filter(function (d) { return exceptions.indexOf(d) === -1; });
        weekendEnables = weekendEnables.concat(exceptions);
      }

      var blockSet = Object.create(null);
      blockouts.forEach(function (d) { blockSet[d] = true; });
      var enabledSet = Object.create(null);
      weekendEnables.forEach(function (d) { enabledSet[d] = true; });

      /* Is this date one we can deliver on?
       *
       * blocked = inBlockoutList || (isWeekend && !explicitlyEnabledWeekend)
       *
       * The generic weekend RULE is load-bearing and must not be regressed to a
       * list lookup. The 'saturdays-sundays' metaobject is hand-maintained and
       * currently ENDS 2026-10-31; anything depending on the list stops blocking
       * weekends on 2026-11-01. A correctness cliff with a date on it. (s6 gave the
       * cart this rule for exactly this reason -- BRIEF 4c trap 2.) */
      function isDeliverable(ymd) {
        if (!isYmd(ymd)) return true;                    // unknown input -> fail open
        if (blockSet[ymd]) return false;
        var dow = weekdayOf(ymd);
        var isWeekend = (dow === 0 || dow === 6);
        if (isWeekend && !enabledSet[ymd]) return false;
        return true;
      }

      // Cutoff: the admin's date_time override wins, but ONLY on the day it names.
      // A stale override (the live one is dated 2025-03-25) must not leak into today.
      var cutoffSource = i.cutoff;
      if (i.cutoffOverride) {
        var ov = null;
        try { ov = wallClock(Date.parse(i.cutoffOverride), tz); } catch (e) { ov = null; }
        if (ov && ov.ymd === now.ymd) {
          cutoffSource = String(ov.hour).padStart(2, '0') + ':' + String(ov.minute).padStart(2, '0');
        }
      }

      var cutoff = parseWallTime(cutoffSource);
      // No parseable cutoff is NOT "midnight" and NOT "23:59" -- those were two of
      // the four different silent fallbacks (AUDIT D5). It is unresolved: fail open.
      if (!cutoff) return unresolved('no-cutoff');

      var openToday = isDeliverable(now.ymd);
      var nowSecs = now.hour * 3600 + now.minute * 60 + now.second;
      var cutSecs = cutoff.hour * 3600 + cutoff.minute * 60;
      var msRemaining = (cutSecs - nowSecs) * 1000;
      // Strictly before: AT the cutoff second, same-day is CLOSED.
      var sameDayOpen = openToday && msRemaining > 0;

      // First deliverable date. Starts today if same-day is still open, else tomorrow.
      // Bounded: an admin who blocks a whole year must not spin the browser.
      var cursor = sameDayOpen ? now.ymd : addDays(now.ymd, 1);
      var guard = 0;
      while (!isDeliverable(cursor) && guard < 400) { cursor = addDays(cursor, 1); guard++; }
      if (guard >= 400) return unresolved('no-deliverable-date-within-400-days');

      return {
        ok: true,
        reason: null,
        today: now.ymd,
        openToday: openToday,
        sameDayOpen: sameDayOpen,
        msRemaining: sameDayOpen ? msRemaining : 0,
        cutoffWall: cutoff,
        nextDeliveryDate: cursor,
        isDeliverable: isDeliverable
      };
    } catch (e) {
      // A thrown exception is the loudest possible "cannot resolve". Fail open.
      return unresolved('threw:' + ((e && e.message) || e));
    }
  }

  /* ---------------------------------------------------------------------------
   * Clock sourcing. Layered, and NEVER silent about which layer answered --
   * callers can see `source` and the test table can pin each layer.
   *
   * Requirement 3 (BRIEF section 3): work even if the visitor's PC clock is wrong.
   * Layers, best first:
   *   1. serverMs   -- an authoritative epoch the visitor cannot skew.
   *   2. device     -- Date.now(). Correct for effectively every real visitor.
   * A device clock that disagrees wildly with the server is reported, not hidden.
   * ------------------------------------------------------------------------- */
  function resolveNow(opts) {
    var o = opts || {};
    if (typeof o.serverMs === 'number' && isFinite(o.serverMs) && o.serverMs > 0) {
      return { nowMs: o.serverMs, source: 'server', skewMs: Date.now() - o.serverMs };
    }
    return { nowMs: Date.now(), source: 'device', skewMs: 0 };
  }

  /* ---------------------------------------------------------------------------
   * HUMAN-READABLE DATES. Pure, deterministic, and exported so the test table can
   * pin them -- Jason's word for what he wanted was "deterministic intelligence,"
   * and the way you keep intelligence deterministic is to put it under test.
   *
   * WHY NOT MM/DD/YYYY (which is what every one of the old five sites emitted):
   * Jason, 2026-07-20 -- "we have European customers ... I generally try to avoid
   * the compressed date formats only because of the varying ways humans keep dates
   * and spell it out in English instead." 07/08 is 7 August to most of the world
   * and July 8th in the US, and a flower order is exactly the wrong place to find
   * out which one the customer assumed.
   *
   * WHY NOT "NEXT WEDNESDAY" (Jason's own suggestion, and I am overriding it -- say
   * so if you disagree): "next Wednesday" is ambiguous in English. In much British
   * and Australian usage it means the Wednesday of NEXT week, not the coming one.
   * Since the entire reason for spelling dates out is international clarity, "Next"
   * would have reintroduced exactly the ambiguity we are removing. Weekday + spelled
   * month + ordinal is unambiguous in every dialect, so the word is dropped:
   *
   *     "Tomorrow"  /  "July 21st"
   *     "Tuesday"   /  "July 28th"
   * ------------------------------------------------------------------------- */
  var MONTHS = ['January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'];
  var WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

  // 1st 2nd 3rd 4th ... 11th 12th 13th ... 21st 22nd 23rd ... 31st.
  // The teens are the trap: 11/12/13 take "th" despite ending in 1/2/3.
  function ordinal(n) {
    if (n % 100 >= 11 && n % 100 <= 13) return n + 'th';
    if (n % 10 === 1) return n + 'st';
    if (n % 10 === 2) return n + 'nd';
    if (n % 10 === 3) return n + 'rd';
    return n + 'th';
  }

  function formatMonthDay(ymd) {
    if (!isYmd(ymd)) return '';
    var b = ymd.split('-');
    return MONTHS[parseInt(b[1], 10) - 1] + ' ' + ordinal(parseInt(b[2], 10));
  }

  function weekdayName(ymd) {
    if (!isYmd(ymd)) return '';
    return WEEKDAYS[weekdayOf(ymd)];
  }

  // "Today" / "Tomorrow" / the weekday. Relative to the STORE's today, never the
  // device's -- a visitor in Sydney must not be told "Tomorrow" about the store's
  // today just because their own date has already rolled over.
  function relativeDayLabel(ymd, todayYmd) {
    if (!isYmd(ymd) || !isYmd(todayYmd)) return weekdayName(ymd);
    if (ymd === todayYmd) return 'Today';
    if (ymd === addDays(todayYmd, 1)) return 'Tomorrow';
    return weekdayName(ymd);
  }

  /* ---------------------------------------------------------------------------
   * fetchServerNow -- the authoritative clock. MEASURED, not assumed.
   *
   * BRIEF section 5 proposed the HTTP `Date` response header from a same-origin fetch
   * and flagged it UNPROVEN with a specific hazard: "check whether the CDN rewrites
   * Date on a cached response, which would put you right back where you started."
   *
   * s7 measured it -- scripts/clock_source_probe.js, 2026-07-20, two passes 120s
   * apart against the live storefront:
   *
   *     document (home)        Date advanced +130.0s   cf-cache-status: DYNAMIC
   *     document (product)     Date advanced +131.0s   cf-cache-status: DYNAMIC
   *     fetch /cart.js         Date advanced +131.0s   cf-cache-status: DYNAMIC
   *     fetch / (cache-bust)   Date advanced +131.0s   cf-cache-status: DYNAMIC
   *
   * Every candidate is LIVE and per-request, no `Age` header anywhere, and the skew
   * against an NTP-synced machine was under 1.1s on all eight reads. The hazard the
   * BRIEF warned about does not materialise: the HTML is not CDN-cached at all.
   *
   * NOTE THE TENSION THIS EXPOSES, because it is not resolved: s6 proved Liquid `now`
   * byte-identical across 70 seconds, which was read as "the page is CDN-cached." A
   * live per-request Date with no Age header says the page is NOT cached. Both
   * measurements are sound, so the staleness of `now` is something OTHER than
   * document caching -- most likely Shopify caching the Liquid render upstream while
   * still emitting a fresh Date. That does not change any decision here (`now` is
   * still useless as a clock, the Date header is still authoritative) but the
   * mechanism is unexplained and is written down rather than glossed.
   *
   * /cart.js is the endpoint: small, JSON, same-origin, and already part of normal
   * cart traffic. Date is CORS-safelisted so it is readable without any header
   * negotiation.
   *
   * THIS IS BELT AND SUSPENDERS, NOT THE CRITICAL PATH. Jason's own framing of
   * requirement 3: a clock wrong enough to break this would also break the TLS
   * handshake, so the visitor could not reach the site at all. Callers therefore
   * decide IMMEDIATELY on the device clock and only re-decide if this disagrees
   * materially. Never block first paint on this fetch.
   * ------------------------------------------------------------------------- */
  function fetchServerNow(url, timeoutMs) {
    return new Promise(function (resolve) {
      if (typeof fetch !== 'function') return resolve(null);
      var done = false;
      var finish = function (v) { if (!done) { done = true; resolve(v); } };
      setTimeout(function () { finish(null); }, timeoutMs || 3000);
      try {
        fetch(url || '/cart.js', { method: 'GET', cache: 'no-store', credentials: 'same-origin' })
          .then(function (r) {
            var d = r.headers.get('date');
            var ms = d ? Date.parse(d) : NaN;
            finish(isFinite(ms) ? ms : null);
          })
          .catch(function () { finish(null); });
      } catch (e) { finish(null); }
    });
  }

  return {
    decide: decide,
    resolveNow: resolveNow,
    fetchServerNow: fetchServerNow,
    formatMonthDay: formatMonthDay,
    weekdayName: weekdayName,
    relativeDayLabel: relativeDayLabel,
    // exported for the test table; not part of the view-facing surface
    _internal: { wallClock: wallClock, weekdayOf: weekdayOf, addDays: addDays, parseWallTime: parseWallTime }
  };
});
